import React, {useRef} from 'react';
import {ListItemSecondaryAction, ListItemText} from '@mui/material';
import {ActionMenuIconButton} from '@netx/core-components/ActionMenuIconButton';
import {
	useArrowFocusItemAction,
	useArrowFocusTrapItem,
} from '@netx/core-components/ArrowFocusTrap';
import {useContextMenu} from '@netx/core-components/ContextMenu';
import {styled} from '@netx/core-components/Theme';
import {
	VirtualizedListItemButton,
	type VirtualizedListItemButtonProps,
} from '@netx/core-components/VirtualizedListItemButton';
import {useSavedCartActions} from '../../hooks';
import type {ISavedCart} from '../../schema';

const SavedCartListItemRoot = styled(VirtualizedListItemButton, {
	name: 'SavedCartListItem',
})(({theme}) => ({
	paddingRight: theme.spacing(6),
}));

export interface SavedCartListItemProps extends VirtualizedListItemButtonProps {
	index: number;
	savedCart: ISavedCart;
}

export const SavedCartListItemPrimitive = ({
	index,
	savedCart,
	...props
}: SavedCartListItemProps) => {
	const rootRef = useRef<HTMLButtonElement>(null);
	const {actions} = useSavedCartActions({id: savedCart.id});
	const contextMenu = useContextMenu();
	const {attributes, setActiveIndex} = useArrowFocusItemAction({index});

	const {
		attributes: itemAttrs,
		handleItemClick,
		handleItemInteractStart,
	} = useArrowFocusTrapItem({index, ref: rootRef});

	return (
		<SavedCartListItemRoot
			ref={rootRef}
			data-id={savedCart.id}
			{...itemAttrs}
			{...props}
			onContextMenu={(event) => {
				event.stopPropagation();
				event.preventDefault();
				contextMenu
					.trigger({
						actions,
						left: event.pageX,
						top: event.pageY,
					})
					.then((action) => action?.cb());
			}}
		>
			<ListItemText
				primary={savedCart.title}
				primaryTypographyProps={{noWrap: true, variant: 'body1'}}
			/>
			<ListItemSecondaryAction>
				{/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
				<div onMouseDown={(e) => e.stopPropagation()}>
					<ActionMenuIconButton
						actions={actions}
						onAction={(action) => action.cb()}
						tabIndex={attributes.tabIndex}
						onClick={() => {
							setActiveIndex(index);
						}}
						IconButtonProps={{
							edge: 'end',
							size: props.dense ? 'small' : 'medium',
							onBlur: attributes.onBlur,
							onFocus: attributes.onFocus,
						}}
						TransitionProps={{
							onEnter: attributes.handleMenuOpen(),
							onExit: attributes.handleMenuClose(),
						}}
					/>
				</div>
			</ListItemSecondaryAction>
		</SavedCartListItemRoot>
	);
};

export const SavedCartListItem = SavedCartListItemPrimitive;

export default SavedCartListItem;
